import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import Modal from 'react-bootstrap/Modal'
import { API_URL } from '../utils/Constants/URLS'
import '../../css/common.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faListCheck,
  faPen,
  faTrashCan,
  faXmark
} from '@fortawesome/free-solid-svg-icons'
import { Table } from 'reactstrap'

const CrossSellModal = props => {
  const token = localStorage.getItem('token')
  const [showModal, setShowModal] = useState(false)
  const [launch, setLaunch] = useState(true)
  const [crossSellData, setCrossSellData] = useState([])
  const [prodStatus, setProdStatus] = useState('')
  const [prodIndex, setProdIndex] = useState(-1)
  const [productLine, setProductLine] = useState('')
  const [modelId, setModelId] = useState('')
  const [isEdit, setIsEdit] = useState(null)
  const [productLineList, setProductLineList] = useState([{}])
  const [models, setModel] = useState([{}])
  const [amount, setAmount] = useState(0)
  const [lp, setLp] = useState(0)
  const [discount, setDiscount] = useState(0)
  const [quantity, setQuantity] = useState(0)
  const [rate, setRate] = useState(0)
  const [committedDeliveryDate, setCommittedDeliveryDate] = useState(null)
  const [standardLeadTime, setStandardLeadTime] = useState('')
  const [stdDuration, setStdDuration] = useState('')
  const [editedCommitedDeliveryDate, setEditedDeliveryDate] = useState('')
  const [editedStandardLeadTime, setEditedStandardLeadTime] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [proposedDealAmount, setProposedDealAmount] = useState('')
  const [editedAmt, setEditedAmt] = useState('')
  const [editedRate, setEditedRate] = useState('')
  const leadDetails = useSelector(store => store.deal)
  const userDetails = useSelector(store => store.user)

  useEffect(() => {
    const fetchProductLines = async () => {
      try {
        const res = await fetch(
          `${API_URL}product-lines?pagination=false&businessLine=${userDetails.businessLine}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'Application/json',
              Authorization: `Bearer ${token}`
            }
          }
        )
        const result = await res.json()
        setProductLineList(
          result.data.docs.map(product => ({
            value: product.id,
            label: product.productLineName
          }))
        )
      } catch (e) {
        console.log(e)
      }
    }
    fetchProductLines()
  }, [token])

  useEffect(() => {
    const fetchLp = async () => {
      try {
        const res = await fetch(
          `${API_URL}models/${modelId}/batches/aggregate`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'Application/json',
              Authorization: `Bearer ${token}`
            }
          }
        )
        const result = await res.json()
        const fetchedLp = result.data.listPrice
        setLp(fetchedLp)
        // Prefill rate with LP when model is selected
        setRate(fetchedLp)
        // Recalculate amount and discount based on prefilled rate and current quantity
        const qty = parseInt(quantity) || 1
        const newAmount = Math.ceil(fetchedLp) * qty
        setAmount(newAmount)
        const disc = ((1 - fetchedLp / fetchedLp) * 100).toFixed(2) // = 0
        setDiscount(disc)
      } catch (e) {
        console.log(e)
      }
    }
    if (modelId) {
      fetchLp()
    }
  }, [token, modelId])

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch(`${API_URL}models/cross-sells`, {
          method: 'POST',
          headers: {
            'Content-Type': 'Application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            productLine,
            leadId: leadDetails.id
          })
        })
        const result = await res.json()
        setModel(
          result.data
            .filter(model => model.added === false)
            .map(model => ({
              listPrice: model.listPrice,
              value: model._id,
              label: model.modelName + ' | ' + model.partCode
            }))
        )
      } catch (e) {
        console.log(e)
      }
    }
    if (productLine !== '') {
      fetchModels()
    }
  }, [token, productLine])

  const handleShowDiv = () => {
    setShowSuccess(true)
    setTimeout(() => {
      setShowSuccess(false)
    }, 3000)
  }

  const handleEditQtyChange = (newQty, data) => {
    const qty = parseInt(newQty) || 1
    if (qty < 1) return
    setQuantity(qty)
    const price = data?.listPrice || 0
    const newAmt = parseFloat(editedRate || (data.rate ?? (data.value / (data.quantity || 1)))) * qty
    setEditedAmt(parseFloat(newAmt.toFixed(2)))
    if (price !== 0) {
      const unitPrice = newAmt / qty
      const calculatedDiscount = ((1 - unitPrice / price) * 100).toFixed(2)
      setDiscount(calculatedDiscount)
    }
  }

  // Calculate amount and discount from rate and quantity
  const calculateFromRate = (listPrice, rateVal, qty) => {
    const price = parseFloat(listPrice) || 0
    const rateNum = parseFloat(rateVal) || 0
    const quantity = parseInt(qty) || 1

    const newAmount = rateNum * quantity
    setAmount(newAmount)

    if (price !== 0) {
      const disc = ((1 - rateNum / price) * 100).toFixed(2)
      setDiscount(disc)
    } else {
      setDiscount(0)
    }
  }

  // Calculate rate and discount from amount and quantity
  const calculateDiscountByAmt = (listPrice, enteredAmount, qty) => {
    const price = parseFloat(listPrice) || 0
    const amt = parseFloat(enteredAmount) || 0
    const quantity = parseInt(qty) || 1

    if (quantity === 0) {
      setDiscount(0)
      setRate(0)
      return
    }

    const unitPrice = amt / quantity
    setRate(parseFloat(unitPrice.toFixed(2)))

    if (price !== 0) {
      const disc = ((1 - unitPrice / price) * 100).toFixed(2)
      setDiscount(disc)
    } else {
      setDiscount(0)
    }
  }

  const addCrossSell = async e => {
    e.preventDefault()
    if (!quantity || Number(quantity) <= 0) return
    try {
      const res = await fetch(`${API_URL}cross-sells/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'Application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          productLine,
          discount,
          quantity,
          standardLeadTime: `${standardLeadTime} ${stdDuration}`,
          committedDeliveryDate,
          rate: parseFloat(rate),
          listPrice: lp,
          model: modelId,
          value: amount,
          leadId: leadDetails.id
        })
      })
      const result = await res.json()
      if (result.error === false) {
        handleShowDiv()
        setCrossSellData([...crossSellData, result.data])
        setLaunch(!launch)
        setProductLine('')
        setModelId('')
        setAmount('')
        setQuantity('')
        setDiscount('')
        setRate(0)
        setLp(0)
        setStandardLeadTime('')
        setStdDuration('')
        setCommittedDeliveryDate('')
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleEditAmt = async (e, id) => {
    e.preventDefault()
    try {
      const res = await fetch(`${API_URL}cross-sells/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'Application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          discount,
          quantity,
          rate: parseFloat(editedRate),
          standardLeadTime: editedStandardLeadTime,
          committedDeliveryDate: editedCommitedDeliveryDate,
          value: editedAmt
        })
      })
      const result = await res.json()
      if (result.error === false) {
        setIsEdit(null)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleShowModal = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleUpdateCrossSell = async (e, value, id, sts, index) => {
    e.preventDefault()
    try {
      const res = await fetch(`${API_URL}cross-sells/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'Application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: sts
        })
      })
      const result = await res.json()
      setProdStatus(result.data.status)
      setProdIndex(index)
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    const fetchCrossSellData = async () => {
      try {
        const res = await fetch(
          `${API_URL}cross-sells?pagination=false&leadId=${leadDetails.id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'Application/json',
              Authorization: `Bearer ${token}`
            }
          }
        )
        const result = await res.json()
        if (result.error === false) {
          setCrossSellData(result?.data?.docs)
          const item = result?.data?.docs?.map(item =>
            item.status === 'added' ? item.value : 0
          )
          setProposedDealAmount(item.reduce((acc, current) => acc + current, 0))
        }
      } catch (e) {
        console.log(e)
      }
    }
    fetchCrossSellData()
  }, [launch, prodStatus, prodIndex, token, isEdit])

  useEffect(() => {
    const addProposedDealAmountToLead = async () => {
      if (proposedDealAmount !== '') {
        try {
          const res = await fetch(`${API_URL}leads/${leadDetails.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'Application/Json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              productRequirements: {
                proposedDealAmount
              }
            })
          })
          const result = await res.json()
        } catch (e) {
          console.log(e)
        }
      }
    }
    addProposedDealAmountToLead()
  }, [proposedDealAmount])

  return (
    <Fragment>
      <div className='d-flex'>
        <span className='z-30'>
          <button
            onClick={handleShowModal}
            className='btn filter-btn text-white'
            type='button'
          >
            <FontAwesomeIcon className='mr-1' icon={faListCheck} />
            Line Items
          </button>
        </span>
      </div>
      <Modal size='xl' show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header className='bg-indigo-200' closeButton>
          <Modal.Title>Products List</Modal.Title>
        </Modal.Header>

        <Modal.Body scrollable={true}>
          <div>
            <div className='p-2'>
              <form
                onSubmit={addCrossSell}
                className='row p-3 bg-light d-flex justify-content-between'
              >
                {/* Product Line */}
                <div className='col-lg-4 col-md-12 col-sm-12'>
                  <label htmlFor='name' className='form-label'>
                    Product Line <span className='text-danger'>*</span>
                  </label>
                  <Select
                    options={productLineList}
                    placeholder='Select Product Line'
                    onChange={e => setProductLine(e.value)}
                  />
                </div>

                {/* Model */}
                <div className='col-lg-4 col-md-12 col-sm-12 cross-sell-form'>
                  <label htmlFor='name' className='form-label'>
                    Model <span className='text-danger'>*</span>
                  </label>
                  {productLine === '' ? (
                    <input type='text' className='form-control' readOnly />
                  ) : (
                    <Select
                      options={models}
                      placeholder='Select Model'
                      onChange={e => {
                        setModelId(e.value)
                      }}
                    />
                  )}
                </div>

                {/* List Price (read-only) */}
                <div className='col-lg-4 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>List Price</label>
                  <input
                    type='number'
                    className='form-control bg-light'
                    value={lp}
                    readOnly
                    tabIndex={-1}
                    style={{ cursor: 'not-allowed', backgroundColor: '#e9ecef' }}
                  />
                </div>

                {/* QTY */}
                <div className='col-lg-3 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    QTY <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    min='1'
                    value={quantity}
                    placeholder='Enter Value'
                    onChange={e => {
                      const newQty = e.target.value
                      setQuantity(newQty)
                      // Recalculate amount using rate * qty
                      const newAmount = parseFloat(rate || 0) * (parseInt(newQty) || 1)
                      setAmount(newAmount)
                    }}
                    onWheel={e => e.target.blur()}
                    className='form-control'
                    required
                  />
                </div>

                {/* Rate (editable) */}
                <div className='col-lg-3 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    Rate <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    placeholder='Enter Rate'
                    className='form-control'
                    value={rate}
                    onWheel={e => e.target.blur()}
                    onChange={e => {
                      const newRate = e.target.value
                      setRate(newRate)
                      calculateFromRate(lp, newRate, quantity)
                    }}
                    required
                    disabled={!quantity || Number(quantity) <= 0}
                  />
                </div>

                {/* Discount */}
                <div className='col-lg-3 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    Discount <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    placeholder='Enter Discount %'
                    className='form-control'
                    value={discount}
                    onWheel={e => e.target.blur()}
                    onChange={e => {
                      const newDisc = e.target.value
                      setDiscount(newDisc)
                      // Calculate rate from discount
                      const price = parseFloat(lp) || 0
                      const newRate = price - (parseFloat(newDisc) / 100) * price
                      const ceilRate = Math.ceil(newRate)
                      setRate(ceilRate)
                      setAmount(ceilRate * (parseInt(quantity) || 1))
                    }}
                    required
                    disabled={!quantity || Number(quantity) <= 0}
                  />
                </div>

                {/* Amount */}
                <div className='col-lg-3 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    Amount <span className='text-danger'>*</span>
                  </label>
                  <input
                    type='number'
                    placeholder='Enter Amount'
                    className='form-control'
                    value={amount}
                    onWheel={e => e.target.blur()}
                    onChange={e => {
                      const newAmt = e.target.value
                      setAmount(newAmt)
                      calculateDiscountByAmt(lp, newAmt, quantity)
                    }}
                    required
                    disabled={!quantity || Number(quantity) <= 0}
                  />
                </div>

                {/* Standard Lead Time */}
                <div className='col-lg-6 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    Standard Lead Time <span className='text-danger'>*</span>
                  </label>
                  <div className='d-flex gap-2'>
                    <input
                      type='number'
                      placeholder='Enter Here'
                      className='form-control'
                      onWheel={e => e.target.blur()}
                      value={standardLeadTime}
                      onChange={e => setStandardLeadTime(e.target.value)}
                      required
                    />
                    <select
                      className='form-select'
                      style={{ maxWidth: '100px' }}
                      value={stdDuration}
                      onChange={e => setStdDuration(e.target.value)}
                      required
                    >
                      <option value=''>Select</option>
                      <option value='Days'>Days</option>
                      <option value='Weeks'>Weeks</option>
                      <option value='Months'>Months</option>
                    </select>
                  </div>
                </div>

                {/* Committed Delivery Date */}
                <div className='col-lg-6 col-md-12 col-sm-12 cross-sell-form'>
                  <label className='form-label'>
                    Commited Delivery Date{' '}
                    <span className='text-danger'>*</span>
                  </label>
                  <input
                    name='meetingDate'
                    min='2022-01-01'
                    max='2099-12-31'
                    className='form-control'
                    type='date'
                    id='delivery data'
                    onClick={e => e.target.showPicker && e.target.showPicker()}
                    value={committedDeliveryDate}
                    onChange={e => setCommittedDeliveryDate(e.target.value)}
                    required
                  />
                </div>

                <div className='col-lg-2 col-md-12 col-sm-12 cross-sell-form mt-2'>
                  <button
                    type='submit'
                    className='btn add-btn text-white'
                    disabled={!quantity || Number(quantity) <= 0}
                  >
                    Add Product
                  </button>
                </div>
                {showSuccess && (
                  <div
                    className={`col-md-12 mt-2 p-3 fade-out bg-green-100 text-green-600`}
                  >
                    Product Added to the list...
                  </div>
                )}
              </form>
            </div>

            <div className='table-responsive sm:overflow-auto sm:max-h-[400px] w-full mb-3'>
              <Table>
                <thead className='table-info'>
                  <tr>
                    <th>Product Line</th>
                    <th>Model</th>
                    <th>Uom</th>
                    <th>LP</th>
                    <th>QTY</th>
                    <th>Rate</th>
                    <th>Discount</th>
                    <th>Amount</th>
                    <th>STD. Lead Time</th>
                    <th>Commited Delivery Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {crossSellData.map((data, index) => {
                    return (
                      <tr key={index}>
                        {/* Product Line */}
                        <td>
                          <span
                            className={
                              data.status === 'added' ? 'enable' : 'disabled'
                            }
                          >
                            {data.productLine.productLineName}
                          </span>
                        </td>

                        {/* Model */}
                        <td>
                          <span
                            className={
                              data.status === 'added' ? 'enable' : 'disabled'
                            }
                          >
                            {data.model.modelName} | {data.model.partCode}
                          </span>
                        </td>

                        <td>
                          <span
                            className={
                              data.status === 'added' ? 'enable' : 'disabled'
                            }
                          >
                            {data.model.uom}
                          </span>
                        </td>

                        {/* LP */}
                        <td>
                          <span
                            className={
                              data.status === 'added' ? 'enable' : 'disabled'
                            }
                          >
                            {data.listPrice}
                          </span>
                        </td>

                        {/* QTY input */}
                        <td>
                          {isEdit === index ? (
                            <input
                              type='number'
                              min='1'
                              className='form-control'
                              style={{ minWidth: '70px' }}
                              value={quantity}
                              onWheel={e => e.target.blur()}
                              onChange={e => handleEditQtyChange(e.target.value, data)}
                            />
                          ) : (
                            <span
                              className={
                                data.status === 'added' ? 'enable' : 'disabled'
                              }
                            >
                              {data.quantity}
                            </span>
                          )}
                        </td>

                        {isEdit === index ? (
                          <>
                            {/* Editable Rate */}
                            <td>
                              <input
                                type='number'
                                className='form-control'
                                value={editedRate}
                                onWheel={e => e.target.blur()}
                                onChange={e => {
                                  const newRate = e.target.value
                                  setEditedRate(newRate)
                                  const price = data?.listPrice || 0
                                  const qty = quantity || data.quantity || 1
                                  const newAmt = parseFloat(newRate) * parseInt(qty)
                                  setEditedAmt(newAmt)
                                  if (price !== 0) {
                                    const calculatedDiscount = (
                                      (1 - parseFloat(newRate) / price) * 100
                                    ).toFixed(2)
                                    setDiscount(calculatedDiscount)
                                  }
                                }}
                              />
                            </td>

                            {/* Editable Discount */}
                            <td>
                              <input
                                type='number'
                                className='form-control'
                                value={discount}
                                onWheel={e => e.target.blur()}
                                onChange={e => {
                                  const newDisc = e.target.value
                                  setDiscount(newDisc)
                                  const price = data?.listPrice || 0
                                  const qty = quantity || data.quantity || 1
                                  const newRate = Math.ceil(
                                    price - (parseFloat(newDisc) / 100) * price
                                  )
                                  setEditedRate(newRate)
                                  setEditedAmt(newRate * parseInt(qty))
                                }}
                              />
                            </td>

                            {/* Editable Amount */}
                            <td>
                              <input
                                type='number'
                                className='form-control'
                                value={editedAmt}
                                onWheel={e => e.target.blur()}
                                onChange={e => {
                                  const newAmt = e.target.value
                                  setEditedAmt(newAmt)
                                  const price = data?.listPrice || 0
                                  const qty = quantity || data.quantity || 1
                                  const unitPrice = parseFloat(newAmt) / parseInt(qty)
                                  setEditedRate(parseFloat(unitPrice.toFixed(2)))
                                  if (price !== 0) {
                                    const calculatedDiscount = (
                                      (1 - unitPrice / price) * 100
                                    ).toFixed(2)
                                    setDiscount(calculatedDiscount)
                                  }
                                }}
                              />
                            </td>

                            {/* Editable STD Lead Time */}
                            <td>
                              <input
                                type='text'
                                className='form-control'
                                value={editedStandardLeadTime}
                                onChange={e =>
                                  setEditedStandardLeadTime(e.target.value)
                                }
                              />
                            </td>

                            {/* Editable Committed Delivery Date */}
                            <td>
                              <input
                                name='meetingDate'
                                min='2022-01-01'
                                max='2099-12-31'
                                className='form-control'
                                type='date'
                                onClick={e =>
                                  e.target.showPicker && e.target.showPicker()
                                }
                                value={editedCommitedDeliveryDate}
                                onChange={e =>
                                  setEditedDeliveryDate(e.target.value)
                                }
                              />
                            </td>

                            {/* Save */}
                            <td className='d-flex'>
                              <button
                                type='submit'
                                className='ml-1 btn add-btn text-white'
                                onClick={e => handleEditAmt(e, data.id)}
                              >
                                Save
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            {/* Rate (display) */}
                            <td>
                              <span
                                className={
                                  data.status === 'added' ? 'enable' : 'disabled'
                                }
                              >
                                {data.rate !== undefined && data.rate !== null
                                  ? parseFloat(data.rate).toFixed(2)
                                  : data.value && data.quantity
                                    ? (data.value / data.quantity).toFixed(2)
                                    : '-'}
                              </span>
                            </td>

                            {/* Discount (display) */}
                            <td>
                              <span
                                className={
                                  data.status === 'added' ? 'enable' : 'disabled'
                                }
                              >
                                {data.discount}%
                              </span>
                            </td>

                            {/* Amount (display) */}
                            <td>
                              <span
                                className={
                                  data.status === 'added' ? 'enable' : 'disabled'
                                }
                              >
                                {data.value.toFixed(2)}
                              </span>
                            </td>

                            {/* STD Lead Time (display) */}
                            <td>
                              <span
                                className={
                                  data.status === 'added' ? 'enable' : 'disabled'
                                }
                              >
                                {data.standardLeadTime}
                              </span>
                            </td>

                            {/* Committed Delivery Date (display) */}
                            <td>
                              <span
                                className={
                                  data.status === 'added' ? 'enable' : 'disabled'
                                }
                              >
                                {new Date(
                                  data.committedDeliveryDate
                                ).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className='d-flex'>
                              {data.status === 'added' ? (
                                <FontAwesomeIcon
                                  size='xs'
                                  onClick={e =>
                                    handleUpdateCrossSell(
                                      e,
                                      data.value,
                                      data.id,
                                      'deleted',
                                      index
                                    )
                                  }
                                  className='p-2 bg-green-200 text-green-600'
                                  icon={faCheck}
                                />
                              ) : (
                                <FontAwesomeIcon
                                  size='xs'
                                  onClick={e =>
                                    handleUpdateCrossSell(
                                      e,
                                      data.value,
                                      data.id,
                                      'added',
                                      index
                                    )
                                  }
                                  className='p-2 bg-yellow-100 text-yellow-600'
                                  icon={faXmark}
                                />
                              )}
                              {data.status === 'added' ? (
                                <FontAwesomeIcon
                                  size='xs'
                                  className='p-2 ml-2 bg-[#e5f6fb] text-[#50c3e6]'
                                  icon={faPen}
                                  onClick={() => {
                                    setIsEdit(index)
                                    setEditedAmt(data.value)
                                    setDiscount(data.discount)
                                    setQuantity(data.quantity)
                                    // Prefill editedRate from data.rate or derive it
                                    const derivedRate =
                                      data.rate !== undefined && data.rate !== null
                                        ? parseFloat(data.rate)
                                        : data.quantity
                                          ? parseFloat(
                                            (data.value / data.quantity).toFixed(2)
                                          )
                                          : 0
                                    setEditedRate(derivedRate)
                                    setEditedDeliveryDate(data.committedDeliveryDate)
                                    setEditedStandardLeadTime(data.standardLeadTime)
                                  }}
                                />
                              ) : (
                                ''
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
            <span>
              Total Deal Value is: {Number(proposedDealAmount)?.toFixed(2)}
            </span>
          </div>
        </Modal.Body>
      </Modal>
    </Fragment>
  )
}

export default CrossSellModal